var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styles from './PhonePage.module.css';
import { useCallback, useRef, useMemo, useState } from 'react';
import { PageLayout } from '../../Components/PageLayout';
import { Text } from '../../Components/BaseComponents/Typography';
import { PhoneInput } from '../../Components/BaseComponents/PhoneInput';
import { observer } from 'mobx-react-lite';
import { useApplication } from '../../Contexts/ApplicationContext';
import { getNonEditableDefaultValue, useSettings, } from '../../Contexts/SettingsContext';
import { useAnalytics } from '../../Contexts/AnalyticsContext';
import { useLocationData } from '../../Contexts/LocationDataContext';
import { storage } from '../../../Services/storage';
import { visibleCaptchaCall, invisibleCaptchaCall, shouldShowVisibleCaptcha, } from './recaptcha';
import { TermsOfServiceCheckbox } from '../../Components/TermsOfServiceCheckbox';
export var PhonePage = observer(function () {
    var _a = useState(false), terms = _a[0], setTerms = _a[1];
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var locationData = useLocationData();
    var orderFacade = useApplication().orderFacade;
    var defaultValues = useSettings().defaultValues;
    var _c = useAnalytics(), track = _c.track, identify = _c.identify;
    var captchaRef = useRef(null);
    var phoneFromSettings = defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.phone;
    var phoneFromSettingsValue = useMemo(function () {
        var _a;
        if (phoneFromSettings && phoneFromSettings.value) {
            var trimValue = (_a = phoneFromSettings.value) === null || _a === void 0 ? void 0 : _a.trim();
            return trimValue[0] === '+' ? trimValue : "+".concat(trimValue);
        }
        return null;
    }, [phoneFromSettings]);
    var _d = useState(orderFacade.userInfo.phone ||
        phoneFromSettingsValue ||
        (locationData === null || locationData === void 0 ? void 0 : locationData.phonePrefix) ||
        ''), phone = _d[0], setPhone = _d[1];
    var _e = useState(undefined), error = _e[0], setError = _e[1];
    var _f = useState(), extendedCaptcha = _f[0], setExtendedCaptcha = _f[1];
    var handleSubmit = useCallback(function () {
        var args = {
            userInfo: {
                phone: phone,
                email: getNonEditableDefaultValue(defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.email),
                targetAddress: getNonEditableDefaultValue(defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.targetAddress),
            },
            extendedCaptcha: Boolean(extendedCaptcha),
            captcha: extendedCaptcha || '',
        };
        setLoading(true);
        invisibleCaptchaCall()
            .then(function (captcha) {
            if (!args.extendedCaptcha) {
                args.captcha = captcha;
            }
            setExtendedCaptcha(undefined);
            return orderFacade.createOrder(args);
        })
            .catch(function (e) {
            return shouldShowVisibleCaptcha(e).then(function () {
                visibleCaptchaCall(captchaRef.current, function (captcha) {
                    return setExtendedCaptcha(captcha);
                });
            });
        })
            .catch(function (errorMessage) {
            setError(errorMessage);
            track('error_raised', { error: errorMessage });
        })
            .finally(function () {
            identify({ phone: phone });
            var orderId = storage.ORDER_ID.get();
            track('phone_submitted', undefined, {
                order_id: orderId,
            });
            setLoading(false);
        });
    }, [
        defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.email,
        defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.targetAddress,
        extendedCaptcha,
        identify,
        orderFacade,
        phone,
        track,
    ]);
    var validate = useCallback(function (callback) {
        if (!phone) {
            var errorText = 'Phone is required';
            setError(errorText);
            track('error_raised', { error: errorText });
        }
        else {
            setError(undefined);
            callback();
        }
    }, [track, phone, setError]);
    var submit = useCallback(function () {
        validate(handleSubmit);
    }, [handleSubmit, validate]);
    var handleChange = useCallback(function (value) {
        setPhone(value.replaceAll(' ', '') || '');
        setError(undefined);
    }, [setError]);
    var showTerms = Boolean(getNonEditableDefaultValue(defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.email));
    return (_jsxs(PageLayout, __assign({ footer: {
            actionButtonLabel: 'Next',
            buttonClick: submit,
            actionButtonLoader: loading,
            actionButtonDisable: showTerms && !terms,
        } }, { children: [_jsx("h2", __assign({ className: styles.title }, { children: "Enter your phone number" })), _jsx(Text, __assign({ margin: "16px 0 40px 0" }, { children: "Your phone number is required to verify your identity. Personal data is encrypted and securely stored." })), _jsx(PhoneInput, { disabled: Boolean(getNonEditableDefaultValue(defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.phone)), value: phone, onChange: handleChange, isError: Boolean(error), helperText: error }), !error && _jsx("div", { ref: captchaRef }), _jsx(TermsOfServiceCheckbox, { hide: !showTerms, onClick: function (checked) { return setTerms(checked); }, checked: terms })] })));
});
